import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../shared/utils.js/utils";

// Financial Periods
export const getFinancialPeriods = createAsyncThunk(
  "FinancialPeriods/getFinancialPeriods",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search !== undefined)
        queryParams.append("search", params.search);
      if (params.status !== undefined)
        queryParams.append("status", params.status);
      if (params.date_from !== undefined)
        queryParams.append("date_from", params.date_from);
      if (params.date_to !== undefined)
        queryParams.append("date_to", params.date_to);
      if (params.sort_by !== undefined)
        queryParams.append("sort_by", params.sort_by);
      if (params.sort_order !== undefined)
        queryParams.append("sort_order", params.sort_order);
      const url = `${BASE_URL}/financial-periods${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get financial periods!";
      return rejectWithValue(message);
    }
  },
);

// get Financial Period Details
export const getFinancialPeriodDetails = createAsyncThunk(
  "FinancialPeriods/getFinancialPeriodDetails",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.get(`${BASE_URL}/financial-periods/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Failed to get financial period details!";
      return rejectWithValue(message);
    }
  },
);

// add Financial Period
export const addFinancialPeriod = createAsyncThunk(
  "FinancialPeriods/addFinancialPeriod",
  async (data, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.post(`${BASE_URL}/financial-periods`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        const message = Object.values(errors).flat().join(", ");
        return rejectWithValue(message || "Validation failed!");
      }
      const message =
        error.response?.data?.message || "Failed to add financial period!";
      return rejectWithValue(message);
    }
  },
);

// update Financial Period
export const updateFinancialPeriod = createAsyncThunk(
  "FinancialPeriods/updateFinancialPeriod",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.put(
        `${BASE_URL}/financial-periods/${id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        const message = Object.values(errors).flat().join(", ");
        return rejectWithValue(message || "Validation failed!");
      }
      const message =
        error.response?.data?.message || "Failed to update financial period!";
      return rejectWithValue(message);
    }
  },
);

// close financial period
export const closeFinancialPeriod = createAsyncThunk(
  "FinancialPeriods/closeFinancialPeriod",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.post(
        `${BASE_URL}/financial-periods/${id}/close`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to close financial period!";
      return rejectWithValue(message);
    }
  },
);

// delete financial period
export const deleteFinancialPeriod = createAsyncThunk(
  "FinancialPeriods/deleteFinancialPeriod",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.delete(
        `${BASE_URL}/financial-periods/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete financial period!";
      return rejectWithValue(message);
    }
  },
);
